import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLazyGetAllUsersQuery,
  useSuspendUserAccountMutation,
  useFreezeUserAccountMutation,
  useDisableUserAccountMutation,
  useActivateUserAccountMutation,
  useDeleteUserMutation,
  useCreateAdminStaffMutation,
} from "../../store/userAccountApi";
import useAuth from "../../hooks/useAuth";
import usePersistedFilters from "../../hooks/usePersistedFilters";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import { DEFAULT_ADMIN_STAFF_FORM } from "./constants";
import { getUserTemplateId, setUserTemplateId } from "./userTemplateStorage";

function getErrorMessage(error, fallback) {
  if (error?.data?.message) return error.data.message;
  if (error?.message) {
    if (error.message.includes("Network Error"))
      return "Bağlantı hatası. İnternet bağlantınızı kontrol edin.";
    if (error.message.includes("401"))
      return "Oturum süreniz dolmuş. Tekrar giriş yapın.";
    if (error.message.includes("403"))
      return "Bu işlem için yetkiniz bulunmamaktadır.";
  }
  return fallback;
}

function sortUsers(list, sortBy, sortOrder) {
  return [...list].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case "createdAt":
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        break;
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "email":
        aVal = a.email.toLowerCase();
        bVal = b.email.toLowerCase();
        break;
      case "accountStatus":
        aVal = a.accountStatus;
        bVal = b.accountStatus;
        break;
      case "lastLogin":
        aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
        break;
      case "role":
        aVal = a.role;
        bVal = b.role;
        break;
      default:
        return 0;
    }
    return sortOrder === "asc"
      ? aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      : aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
  });
}

const DEFAULT_FILTERS = { searchName: "", searchEmail: "", searchTcNo: "", status: "", role: "" };

export default function useUserManagement({ scopeRoles, scopeKey } = {}) {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [getAllUsers, { data: usersData, isLoading: isLoadingUsersInitial, isFetching: isFetchingUsers, error: usersError }] =
    useLazyGetAllUsersQuery();
  const isLoadingUsers = isLoadingUsersInitial || isFetchingUsers;
  const [suspendAccount, { isLoading: isSuspending }] = useSuspendUserAccountMutation();
  const [freezeAccount, { isLoading: isFreezing }] = useFreezeUserAccountMutation();
  const [disableAccount, { isLoading: isDisabling }] = useDisableUserAccountMutation();
  const [activateAccount, { isLoading: isActivating }] = useActivateUserAccountMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [createAdminStaff] = useCreateAdminStaffMutation();

  const isActionLoading = isSuspending || isFreezing || isDisabling || isActivating;

  const [users, setUsers] = useState([]);

  // Filtreler sessionStorage'da, scope'a göre AYRI anahtarla: Hastalar/Klinik
  // Kullanıcıları/Personeller bağlamları birbirinin filtresini ezmesin.
  const [filters, setFilters] = usePersistedFilters(`usersList:${scopeKey || "all"}:filters`, DEFAULT_FILTERS);
  const [page, setPage] = useState(1); // 1-tabanlı
  const [pageSize, setPageSize] = useState(10); // ListView gövdesinden ölçülür

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const nameSearch = useDebouncedSearch(filters.searchName);
  const emailSearch = useDebouncedSearch(filters.searchEmail);
  // Eski oturum kayıtlarında alan yok → boş kabul.
  const tcSearch = useDebouncedSearch(filters.searchTcNo || "");
  const debouncedSearchName = nameSearch.debounced;
  const debouncedSearchEmail = emailSearch.debounced;
  const debouncedSearchTcNo = tcSearch.debounced;
  const pending = {
    searchName: nameSearch.pending,
    searchEmail: emailSearch.pending,
    searchTcNo: tcSearch.pending,
  };

  const setFilter = useCallback(
    (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(1);
    },
    [setFilters]
  );

  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setPage(1);
  }, [setFilters]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [reason, setReason] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const [adminStaffDrawerOpen, setAdminStaffDrawerOpen] = useState(false);
  const [adminStaffForm, setAdminStaffForm] = useState({ ...DEFAULT_ADMIN_STAFF_FORM });
  const [isCreatingAdminStaff, setIsCreatingAdminStaff] = useState(false);
  const [adminStaffFormError, setAdminStaffFormError] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => {
    setSnackbar((p) => ({ ...p, open: false }));
  }, []);

  useEffect(() => {
    if (usersData?.data?.users) {
      setUsers(
        usersData.data.users.map((u) => ({
          id: u._id,
          name: u.name,
          surname: u.surname || u.lastName,
          email: u.email,
          phone: u.tellNo || u.tel || u.phone,
          role: u.role,
          accountStatus: u.accountStatus || "Aktif",
          statusReason: u.statusReason,
          suspensionEndDate: u.suspensionEndDate,
          freezeEndDate: u.freezeEndDate,
          tcNo: u.tcNo,
          createdAt: u.createdInfo || u.createdAt,
          lastLogin: u.lastLogin,
          clinic: u.clinic
            ? { id: u.clinic._id, name: u.clinic.name, status: u.clinic.status, logoUrl: u.clinic.logoUrl || null, logoIconUrl: u.clinic.logoIconUrl || null }
            : null,
        }))
      );
    }
  }, [usersData]);

  // Scope rolleri JSON-key — array değişimini stable referansa çevir
  const scopeRolesKey = Array.isArray(scopeRoles) && scopeRoles.length > 0 ? JSON.stringify(scopeRoles) : "";
  const refreshUsers = useCallback(() => {
    if (!token) return;
    const body = { token };
    if (scopeRolesKey) body.roles = JSON.parse(scopeRolesKey);
    getAllUsers(body).unwrap().catch((e) => showSnackbar(getErrorMessage(e, "Kullanıcı listesi yüklenirken hata oluştu!"), "error"));
  }, [token, scopeRolesKey, getAllUsers, showSnackbar]);

  useEffect(() => { refreshUsers(); }, [refreshUsers]);

  const counts = useMemo(() => ({
    total: users.length,
    Aktif: users.filter((u) => u.accountStatus === "Aktif").length,
    "Askıya Alındı": users.filter((u) => u.accountStatus === "Askıya Alındı").length,
    Donduruldu: users.filter((u) => u.accountStatus === "Donduruldu").length,
    "Devre Dışı": users.filter((u) => u.accountStatus === "Devre Dışı").length,
  }), [users]);

  const filteredUsers = useMemo(() => {
    const nameText = debouncedSearchName.trim().toLowerCase();
    const emailText = debouncedSearchEmail.trim().toLowerCase();
    const tcText = debouncedSearchTcNo.replace(/\D/g, "");
    const filtered = users.filter((u) => {
      if (nameText && !(u.name || "").toLowerCase().includes(nameText) && !(u.surname || "").toLowerCase().includes(nameText)) return false;
      if (emailText && !(u.email || "").toLowerCase().includes(emailText)) return false;
      if (tcText && !String(u.tcNo || "").includes(tcText)) return false;
      if (filters.status && u.accountStatus !== filters.status) return false;
      // Scope sabit rol kısıtlaması (örn. Personeller scope'unda her zaman sadece AdminStaff)
      if (Array.isArray(scopeRoles) && scopeRoles.length > 0 && !scopeRoles.includes(u.role)) {
        return false;
      }
      if (filters.role) {
        if (filters.role.startsWith("tpl:")) {
          const tplId = filters.role.slice(4);
          if (getUserTemplateId(u.email) !== tplId) return false;
        } else if (u.role !== filters.role) {
          return false;
        }
      }
      return true;
    });
    return sortUsers(filtered, sortBy, sortOrder);
  }, [users, debouncedSearchName, debouncedSearchEmail, debouncedSearchTcNo, filters.status, filters.role, scopeRoles, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));

  // pageSize ölçüm sonrası (pencere büyütülür/sürüklenerek yeniden boyutlandırılır)
  // totalPages düşebilirken saklanan `page` state'i yüksek kalabilir. Clamp'i
  // türetilmiş değer olarak render sırasında yapıyoruz ki boyamadan sonra
  // (effect'te) düzeltilene kadar boş bir kare ("Sonuç bulunamadı" / ters
  // aralık) görünmesin. Saklanan `page` state'i bilerek uzlaştırılmıyor: kasıtlı
  // olarak yüksek tutulur, böylece pageSize geçici olarak küçülüp tekrar
  // büyüdüğünde (sürükleyerek yeniden boyutlandırma) kullanıcı kaldığı sayfaya
  // geri döner — raw state'i clamp'leyen bir effect bu bilgiyi kalıcı olarak
  // kaybederdi.
  const safePage = Math.max(1, Math.min(page, totalPages));

  const pagedUsers = useMemo(
    () => filteredUsers.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredUsers, safePage, pageSize]
  );

  const handleSortChange = useCallback((col) => {
    setSortBy((prev) => {
      if (prev === col) { setSortOrder((o) => (o === "asc" ? "desc" : "asc")); return prev; }
      setSortOrder("desc");
      return col;
    });
    setPage(1);
  }, []);

  const openMenu = useCallback((e, user) => { setMenuAnchorEl(e.currentTarget); setMenuUser(user); }, []);
  const closeMenu = useCallback(() => { setMenuAnchorEl(null); setMenuUser(null); }, []);

  const openDetailFor = useCallback((user) => {
    setSelectedUser(user);
    setDetailDrawerOpen(true);
    closeMenu();
  }, [closeMenu]);

  const openDetail = useCallback(() => openDetailFor(menuUser), [menuUser, openDetailFor]);

  const openActionDialog = useCallback((type) => {
    setSelectedUser(menuUser); setDialogType(type); setDialogOpen(true); setReason(""); setEndDate(null); closeMenu();
  }, [menuUser, closeMenu]);

  const closeActionDialog = useCallback(() => {
    setDialogOpen(false); setSelectedUser(null); setDialogType(""); setReason(""); setEndDate(null);
  }, []);

  const handleAccountAction = useCallback(async () => {
    if (!selectedUser || !token) return;
    try {
      const payload = { token, id: selectedUser.id, reason: reason.trim(), ...(endDate && { endDate: endDate.toISOString() }) };
      switch (dialogType) {
        case "suspend":
          if (!reason.trim()) { showSnackbar("Askıya alma sebebi zorunludur", "error"); return; }
          await suspendAccount(payload).unwrap(); showSnackbar("Kullanıcı hesabı askıya alındı"); break;
        case "freeze":
          if (!reason.trim()) { showSnackbar("Dondurma sebebi zorunludur", "error"); return; }
          await freezeAccount(payload).unwrap(); showSnackbar("Kullanıcı hesabı donduruldu"); break;
        case "disable":
          if (!reason.trim()) { showSnackbar("Devre dışı bırakma sebebi zorunludur", "error"); return; }
          await disableAccount({ token, id: selectedUser.id, reason: reason.trim() }).unwrap(); showSnackbar("Kullanıcı hesabı devre dışı bırakıldı"); break;
        case "activate":
          await activateAccount({ token, id: selectedUser.id }).unwrap(); showSnackbar("Kullanıcı hesabı aktifleştirildi"); break;
        default: return;
      }
      refreshUsers(); closeActionDialog();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "İşlem sırasında bir hata oluştu"), "error");
    }
  }, [selectedUser, token, dialogType, reason, endDate, suspendAccount, freezeAccount, disableAccount, activateAccount, refreshUsers, closeActionDialog, showSnackbar]);

  const openDeleteDialog = useCallback(() => {
    setDeleteTargetUser(menuUser); setDeleteDialogOpen(true); closeMenu();
  }, [menuUser, closeMenu]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetUser || !token) return;
    try {
      await deleteUser({ token, id: deleteTargetUser.id }).unwrap();
      showSnackbar("Kullanıcı silindi"); setDeleteDialogOpen(false); setDeleteTargetUser(null); refreshUsers();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Kullanıcı silinirken bir hata oluştu"), "error");
    }
  }, [deleteTargetUser, token, deleteUser, refreshUsers, showSnackbar]);

  const setAdminStaffField = useCallback((field, value) => {
    setAdminStaffForm((p) => ({ ...p, [field]: value }));
  }, []);

  const openAdminStaffDrawer = useCallback(() => {
    setAdminStaffForm({ ...DEFAULT_ADMIN_STAFF_FORM });
    setAdminStaffFormError("");
    setAdminStaffDrawerOpen(true);
  }, []);

  const closeAdminStaffDrawer = useCallback(() => {
    setAdminStaffDrawerOpen(false);
    setAdminStaffFormError("");
  }, []);

  const handleCreateAdminStaff = useCallback(async () => {
    setAdminStaffFormError("");
    const { name, surname, email, password, confirmPassword, tellNo, tcNo, roleTemplateId } = adminStaffForm;
    if (!name.trim()) { setAdminStaffFormError("Ad alanı zorunludur"); return; }
    if (!surname.trim()) { setAdminStaffFormError("Soyad alanı zorunludur"); return; }
    if (!email.trim()) { setAdminStaffFormError("E-posta alanı zorunludur"); return; }
    if (!password.trim()) { setAdminStaffFormError("Şifre alanı zorunludur"); return; }
    if (password !== confirmPassword) { setAdminStaffFormError("Şifreler eşleşmiyor"); return; }
    if (!tellNo.trim()) { setAdminStaffFormError("Telefon numarası zorunludur"); return; }
    if (!tcNo.trim()) { setAdminStaffFormError("TC kimlik numarası zorunludur"); return; }
    setIsCreatingAdminStaff(true);
    try {
      await createAdminStaff({ token, ...adminStaffForm }).unwrap();
      // localStorage'a email→templateId mapping kaydet
      if (roleTemplateId) setUserTemplateId(email, roleTemplateId);
      showSnackbar("Personel başarıyla oluşturuldu", "success");
      setAdminStaffDrawerOpen(false);
      setAdminStaffFormError("");
      refreshUsers();
    } catch (err) {
      setAdminStaffFormError(getErrorMessage(err, "Personel oluşturulurken hata oluştu"));
    } finally { setIsCreatingAdminStaff(false); }
  }, [token, adminStaffForm, createAdminStaff, refreshUsers, showSnackbar]);

  return {
    token,
    // Yazarken (debounce) de iskelet: eski liste bir an bile görünmesin.
    isLoadingUsers: isLoadingUsers || pending.searchName || pending.searchEmail || pending.searchTcNo,
    usersError,
    users,
    filteredUsers,
    pagedUsers,
    counts,
    refreshUsers,

    filters, setFilter, clearFilters, pending,
    page: safePage, setPage, totalPages,
    pageSize, setPageSize,

    sortBy, sortOrder, handleSortChange,

    menuAnchorEl, menuUser, openMenu, closeMenu,
    openDetail, openDetailFor,
    detailDrawerOpen, setDetailDrawerOpen,
    selectedUser,

    dialogOpen, dialogType, reason, setReason, endDate, setEndDate,
    openActionDialog, closeActionDialog, handleAccountAction,
    isActionLoading,

    deleteDialogOpen, setDeleteDialogOpen,
    deleteTargetUser, setDeleteTargetUser,
    openDeleteDialog, handleDeleteConfirm, isDeleting,

    adminStaffDrawerOpen, setAdminStaffDrawerOpen,
    adminStaffForm, setAdminStaffField,
    openAdminStaffDrawer, closeAdminStaffDrawer, handleCreateAdminStaff, isCreatingAdminStaff,
    adminStaffFormError, setAdminStaffFormError,

    snackbar, showSnackbar, closeSnackbar,
  };
}
